import { useState } from 'react'
import { Banner, Field, Input, Select } from '@/ui'
import { errorMessage } from '../../api/core'
import { useOpenDelabelRun } from '../../api/lab'
import type { UserOption } from '../../api/reference'
import { useEquipment } from '../../api/setup'
import { useLocations } from '../../api/store'
import { delabelBody, emptyDelabelDraft, validateDelabel, type DelabelDraft } from '../../model/lab/labLimitForm'
import { FormDialog } from '../FormDialog'
import { PersonPicker } from '../PersonPicker'

/**
 * Open a de-label run (F7-B1.c-b) for a pack lot turned ordinary: the pouches are emptied at the
 * hall and the film destroyed; outputs and the close come from the phone. The server needs a
 * turn-ordinary disposition on the pack lot first and says so.
 */
export function DelabelRunDialog({ packLotId, packLotCode, onClose }: { packLotId: string; packLotCode: string; onClose: () => void }) {
  const [d, setD] = useState<DelabelDraft>(emptyDelabelDraft)
  const [operator, setOperator] = useState<UserOption | null>(null)
  const [touched, setTouched] = useState(false)
  const halls = useLocations({ level: 'SITE', kind: 'PACKING', page: 1, limit: 100 })
  const equipment = useEquipment({ page: 1, limit: 200, isActive: true })
  const open = useOpenDelabelRun()
  const p = validateDelabel(d)
  const ok = Object.values(p).every((v) => !v)

  return (
    <FormDialog
      open
      onClose={onClose}
      title={`De-label run · ${packLotCode}`}
      description="The pouches are cut open and emptied into ordinary stock, and the printed film is destroyed. Record what comes out and close the run on the phone."
      saveLabel="Open run"
      loading={open.isPending}
      onSave={async () => {
        setTouched(true)
        if (!ok) throw new Error('invalid')
        await open.mutateAsync({ packLotId, body: delabelBody(d) })
      }}
    >
      {open.isError && (
        <Banner tone="error" title="Run not opened">
          {errorMessage(open.error)}
        </Banner>
      )}
      <Field label="Who empties the pouches" required error={touched ? p.operatorId : undefined}>
        <PersonPicker
          label="Operator"
          value={operator}
          onChange={(u) => {
            setOperator(u)
            setD({ ...d, operatorId: u?.id ?? '' })
          }}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Packing hall" required error={touched ? p.locationId : undefined}>
          <Select
            value={d.locationId}
            onChange={(locationId) => setD({ ...d, locationId })}
            placeholder={halls.isLoading ? 'Loading…' : 'Choose…'}
            options={(halls.data?.rows ?? []).map((l) => ({ value: l.id, label: `${l.name} (${l.code})` }))}
          />
        </Field>
        <Field label="Machine" hint="Optional">
          <Select
            value={d.equipmentId}
            onChange={(equipmentId) => setD({ ...d, equipmentId })}
            placeholder="None"
            options={[
              { value: '', label: 'None' },
              ...(equipment.data?.rows ?? []).map((e) => ({ value: e.id, label: `${e.name} (${e.code})` })),
            ]}
          />
        </Field>
      </div>
      <Field label="Kilos of pouches emptied" required error={touched ? p.inputKg : undefined}>
        <Input
          inputMode="decimal"
          value={d.inputKg}
          onChange={(e) => setD({ ...d, inputKg: e.target.value })}
          trailing="kg"
          placeholder="26.4"
          className="font-mono"
        />
      </Field>
    </FormDialog>
  )
}
